import {
  ArrowUpIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { Button } from "@/core/components/ui/button";
import { cn } from "@/core/utils/utils";
import { EDITOR_LIMITS } from "@/payroll/editor/constants";
import type { Outgoing } from "./use-outgoing";

const QUEUE_ACTION =
  "h-6 w-6 min-w-6 shrink-0 rounded-md text-tertiary-text hover:bg-surface-primary hover:text-primary-text";

/**
 * Messages written while a request was running, waiting their turn.
 *
 * Editing happens on the row. It used to pull the message back into the
 * composer, which threw away whatever the owner had already typed there and
 * left them with two half-written changes and one box to hold them.
 */
export const QueuedMessages = ({
  queued,
  onSendNow,
  onEdit,
  onRemove,
}: {
  queued: Outgoing[];
  onSendNow: (id: string) => void;
  onEdit: (id: string, message: string) => void;
  onRemove: (id: string) => void;
}) => {
  const [opened, setOpened] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const save = () => {
    if (editing && draft.trim()) onEdit(editing, draft.trim());
    setEditing(null);
  };
  if (!queued.length) return null;
  // One stack, tucked behind the composer and inset from it, so it reads as
  // paper underneath the thing you are typing in rather than as its own list.
  // No rules between the rows: three of them already read as a list, and the
  // lines made each one look like a card again. The last row ends flush with
  // the composer's top edge, so none of the stack's own background shows
  // through as a half-row.
  return (
    <div className="mx-5 -mb-8 rounded-t-xl border border-b-0 border-stroke-tertiary bg-surface-primary pb-6 text-sm">
      {/* The scroll lives inside the tuck, not across it. With one element
              doing both, the last row scrolled into the strip that hides behind
              the composer and could not be read or reached. */}
      <ul className="max-h-48 overflow-y-auto [scrollbar-gutter:stable] [scrollbar-width:thin]">
        {queued.map((item) => (
          <li key={item.id} className="flex items-start gap-1 py-0.5 pl-3 pr-1">
            {editing === item.id ? (
              // No form in here, and no submit button. This list renders
              // inside the composer's own form, so a nested one submitted both
              // and Save on a queued message sent whatever was in the composer
              // as a new request. Sending has to be the owner's own decision.
              <div className="flex min-w-0 flex-1 items-start gap-1 py-0.5">
                <textarea
                  autoFocus
                  rows={2}
                  maxLength={EDITOR_LIMITS.messageLength}
                  value={draft}
                  aria-label="Edit queued message"
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") setEditing(null);
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      save();
                    }
                  }}
                  className="min-w-0 flex-1 resize-none rounded-md border border-stroke-tertiary bg-surface-primary px-2 py-1 outline-none"
                />
                <Button
                  type="button"
                  size="xs"
                  variant="tertiary"
                  onClick={save}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  size="xs"
                  variant="text"
                  onClick={() => setEditing(null)}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                {/* The row opens to its full text. A queued message is often a
                  sentence longer than the column, and reading it should not
                  mean pulling it back into the composer to look. */}
                <button
                  type="button"
                  className="min-w-0 flex-1 py-1 text-left text-secondary-text"
                  aria-expanded={opened === item.id}
                  title={item.message}
                  onClick={() => setOpened(opened === item.id ? null : item.id)}
                >
                  {/* Opened, a long message is capped and scrolls. The limit is
                    20,000 characters, and one of those would otherwise push the
                    conversation off the screen. */}
                  <span
                    className={cn("block", {
                      "max-h-32 overflow-y-auto whitespace-pre-wrap":
                        opened === item.id,
                      truncate: !(opened === item.id),
                    })}
                  >
                    {item.message}
                  </span>
                  <span className="sr-only">
                    Queued. Sends when the current request finishes.
                  </span>
                </button>
                <Button
                  type="button"
                  size="iconSm"
                  variant="ghost"
                  className={QUEUE_ACTION}
                  title="Send now, stopping the current request"
                  aria-label={`Send "${item.message}" now`}
                  onClick={() => onSendNow(item.id)}
                >
                  <ArrowUpIcon className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  size="iconSm"
                  variant="ghost"
                  className={QUEUE_ACTION}
                  title="Edit"
                  aria-label={`Edit "${item.message}"`}
                  onClick={() => {
                    setDraft(item.message);
                    setEditing(item.id);
                  }}
                >
                  <PencilIcon className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  size="iconSm"
                  variant="ghost"
                  className={QUEUE_ACTION}
                  title="Remove"
                  aria-label={`Remove "${item.message}"`}
                  onClick={() => onRemove(item.id)}
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
